import React, { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';

const SCENES = [
  { type: 'video', src: '/intro/intro-engineering.mp4', caption: 'Every part. Every revision. One digital thread.' },
  { type: 'image', src: '/intro/screen-dashboard.jpg', caption: 'Live configuration management dashboard', ms: 2500 },
  { type: 'image', src: '/intro/screen-vault.jpg', caption: 'Controlled file vault with check-in / check-out', ms: 2500 },
  { type: 'image', src: '/intro/screen-3d.jpg', caption: 'STEP & IGES models open straight in 3D', ms: 2500 },
  { type: 'image', src: '/intro/screen-setup.jpg', caption: 'Enterprise setup before your data arrives', ms: 2500 },
  { type: 'video', src: '/intro/intro-hardware.mp4', caption: 'From design to launch — BaselinX PLM' },
];

function Scene({ scene, active, paused, onDone }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (scene.type !== 'video' || !v) return;
    if (active && !paused) { v.play().catch(onDone); } else v.pause();
    if (active && v.ended) { v.currentTime = 0; }
  }, [active, paused, scene.type, onDone]);
  useEffect(() => {
    if (scene.type === 'video' && active && ref.current) ref.current.currentTime = 0;
  }, [active, scene.type]);
  const cls = `absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${active ? 'opacity-100' : 'opacity-0'}`;
  if (scene.type === 'video') {
    return <video ref={ref} src={scene.src} className={cls} muted playsInline preload={active ? 'auto' : 'metadata'} onEnded={onDone} onError={active ? onDone : undefined} aria-hidden="true" />;
  }
  return <img src={scene.src} alt="" className={`${cls} object-left-top ${active && !paused ? 'intro-kenburns' : ''}`} aria-hidden="true" />;
}

/** ~30s looping hero reel: AI cinematic clips + real product screens, muted, behind the hero copy. */
export const IntroReel = ({ poster }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduced] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  const next = useRef(() => {});
  next.current = () => setIdx((i) => (i + 1) % SCENES.length);
  const onDone = useRef(() => next.current()).current;
  const scene = SCENES[idx];
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let t;
    const go = () => { t = setTimeout(() => setReady(true), 800); };
    if (document.readyState === 'complete') go(); else window.addEventListener('load', go, { once: true });
    return () => { clearTimeout(t); window.removeEventListener('load', go); };
  }, []);

  useEffect(() => {
    if (!ready || reduced || paused || scene.type !== 'image') return undefined;
    const t = setTimeout(onDone, scene.ms);
    return () => clearTimeout(t);
  }, [idx, paused, reduced, scene, onDone, ready]);

  if (reduced || !ready) return <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="intro-reel-poster" />;
  return (
    <div className="absolute inset-0 bg-slate-950" data-testid="intro-reel">
      {SCENES.map((s, i) => <Scene key={s.src} scene={s} active={i === idx} paused={paused} onDone={onDone} />)}
      <div className="absolute bottom-6 right-6 z-20 flex items-center gap-3">
        <span key={idx} className="hidden sm:block text-xs text-slate-200 bg-slate-900/70 backdrop-blur-md border border-cyan-500/30 rounded-full px-4 py-1.5 intro-caption" data-testid="intro-reel-caption">
          {scene.caption}
        </span>
        <div className="flex gap-1" data-testid="intro-reel-progress">
          {SCENES.map((s, i) => (
            <button key={s.src} onClick={() => setIdx(i)} aria-label={`Scene ${i + 1}`} data-testid={`intro-reel-dot-${i}`}
              className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${i === idx ? 'w-6 bg-cyan-400' : 'w-1.5 bg-slate-500 hover:bg-slate-300'}`} />
          ))}
        </div>
        <button onClick={() => setPaused((p) => !p)} aria-label={paused ? 'Play intro' : 'Pause intro'} data-testid="intro-reel-toggle"
          className="w-8 h-8 rounded-full bg-slate-900/70 border border-slate-600 text-slate-200 flex items-center justify-center hover:border-cyan-400 transition-colors">
          {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
};
